import React from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { SliceEndorsmentBoxData } from '@contracts';
import useReplacedInnerHtml from '@utils/useReplacedInnerHtml';

interface Props {
  data: SliceEndorsmentBoxData;
  backgroundColor: string;
}

const EndorsmentBox: React.FC<Props> = ({ data, backgroundColor }) => {
  const image = getImage(data.profile_image);
  const nameText = data.name?.text;
  const qualificationText = data.qualification?.text;
  const quoteText = data.quote?.text;
  const titleText = data.title?.text;
  const quoteHtml = useReplacedInnerHtml(quoteText);

  return (
    <Container $backgroundColor={backgroundColor}>
      <ImageWrap>{image && <GatsbyImage image={image} alt={data.profile_image?.alt ?? ''} objectFit="contain" />}</ImageWrap>
      <TextWrap $hasNameAndTitle={Boolean(nameText && titleText)}>
        <span dangerouslySetInnerHTML={{ __html: quoteHtml }} />
        <NameWrap>
          {nameText && <b>{data.name?.text}</b>}
          {qualificationText && <b style={{ opacity: '0.5', fontWeight: '400' }}>{qualificationText}</b>}
        </NameWrap>
        {titleText && <span>{titleText}</span>}
      </TextWrap>
    </Container>
  );
};

export default EndorsmentBox;

interface StyleProps {
  $backgroundColor?: string;
}

const Container = styled.div<StyleProps>`
  display: flex;
  align-items: center;
  min-width: 580px;
  max-width: 800px;
  background: ${({ $backgroundColor }) => $backgroundColor || Colors.mint};
  border-radius: 2px;
  padding: 30px 50px 40px 50px;

  @media (max-width: 1024px) {
    width: 100%;
    width: calc(100% - 40px);
    height: 100%;
    min-width: auto;
    margin-inline: 20px;

    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: 30px;
  }
`;

const TextWrap = styled.div<{ $hasNameAndTitle: boolean }>`
  display: flex;
  flex-direction: column;
  max-width: 786px;
  gap: 20px;

  font-family: BrownStd;
  font-size: ${(props) => (props.$hasNameAndTitle ? '16px' : '22px')};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  letter-spacing: 0;
  text-align: left;

  span:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 1024px) {
    text-align: center;
    max-width: 100%;
    margin-top: 20px;
  }
`;

const NameWrap = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;

  &:empty {
    display: none;
  }

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const ImageWrap = styled.div`
  margin-right: 40px;
  min-width: 129px;
  max-width: 129px;
  max-height: 129px;
  display: flex;
  justify-content: center;
  align-items: baseline;

  img {
    border-radius: 100%;
  }

  @media (max-width: 1024px) {
    width: 100px;
    margin-right: 0;
  }
`;
